import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { getRpmLeaderboardUseCase } from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { RpmLeaderboardEntry } from '@/src/domain/models/TrainingLaunch';
import { useTranslation } from '@/src/i18n';
import { LeaderboardScreen, TabType } from '@/src/presentation/screens/LeaderboardScreen/LeaderboardScreen';
import { Stack, useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';

export default function LeaderboardContainer() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const router = useRouter();

  const [entries, setEntries] = useState<RpmLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('score');

  const loadLeaderboard = useCallback(async (sortBy: TabType, isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await getRpmLeaderboardUseCase.execute(sortBy, 50);
      setEntries(data);
    } catch (error) {
      logger.error('Leaderboard', 'Failed to load leaderboard', { error });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard(activeTab);
  }, [activeTab]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleRefresh = () => {
    loadLeaderboard(activeTab, true);
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const screenColors = {
    ...colors,
    primarySubtle: colors.primary + '18',
    tabActive: colors.primary,
    tabInactive: colors.textSecondary,
  };

  return (
    <>
      <Stack.Screen options={{ title: t('training.leaderboardTitle') }} />
      <LeaderboardScreen
        entries={entries}
        loading={loading}
        refreshing={refreshing}
        currentUserId={user?.id}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onRefresh={handleRefresh}
        onUserPress={handleUserPress}
        colors={screenColors}
      />
    </>
  );
}
